import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type FocusEvent,
} from "react";
import {
  focusManager,
  onlineManager,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
  updatePaymentAuthenticationForError,
} from "@bw/shared/api";
import {
  useAuth,
  useWaiverGate,
  useFrontendConfig,
  usePaymentMethods,
  useWalletSetupIntent,
  paymentQueryKeys,
} from "@bw/shared/hooks";
import type {
  DropInCheckInRequest,
  DropInPaymentOption,
} from "@bw/shared/types";
import type {
  DropInSessionResponse,
  SavedPaymentMethodResponse,
} from "@bw/shared/types/generated";
import { canonicalizeDropInPaymentOptions } from "@bw/shared/utils";
import { Page } from "../../../common/components/Page";
import { PaymentMethodsLoadError } from "../../../common/components/PaymentMethodsLoadError";
import {
  MaterialAddCardForm,
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
  MaterialPaymentMethodPicker,
  MaterialWaiverGate,
} from "../../../common/components/payment";
import { queryKeys } from "../../../common/query/queryClient";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { useDropInPaymentOptions } from "../hooks/useDropInPaymentOptions";
import { useDropInCourtState } from "../hooks/useDropInCourtState";
import { useDropInSession } from "../hooks/useDropInSession";
import { useCheckIn } from "../hooks/useCheckIn";
import { useFacilityMode } from "../hooks/useFacilityMode";
import {
  admissionUnavailableMessage,
  PaymentMethodSelect,
  type PaymentSelection,
} from "../components/PaymentMethodSelect";
import { FacilityCommunityCard } from "../components/FacilityCommunityCard";
import { DropInCheckInHeader } from "../components/DropInCheckInHeader";
import { dropInPricePresentation } from "../dropInPricePresentation";
import { DropInOrderSummary } from "../components/DropInOrderSummary";

/**
 * Map a check-in failure to honest inline copy. The backend signals are:
 *   409 NO_ACTIVE_DROP_IN  → the window isn't active
 *   409 ALREADY_CHECKED_IN → the player already has a session
 *   422 INVALID_PAYMENT_METHOD       → missing/invalid payment input
 *   409 DROPIN_PRICE_CHANGED         → refresh and review the latest quote
 *   409 DROPIN_POLICY_CHANGED        → refresh and review the latest terms
 *   428 DROPIN_WAIVER_CHANGED_AFTER_PAYMENT → reversal; start a new attempt
 *   422 FACILITY_PAYMENTS_UNAVAILABLE → facility payment setup is unavailable
 *   503 DROPIN_PAYMENT_UNCERTAIN      → retry only with the original identity
 * Anything else falls back to a generic retry message (never a blank crash).
 */
function mapCheckInError(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === "ALREADY_CHECKED_IN") {
      return "You're already checked in here.";
    }
    if (err.code === "NO_ACTIVE_DROP_IN") {
      const minimumRemaining = err.details?.minimum_remaining_minutes;
      if (
        err.details?.reason === "closing_soon" &&
        typeof minimumRemaining === "number" &&
        Number.isInteger(minimumRemaining) &&
        minimumRemaining > 0
      ) {
        return `New check-ins are closed because fewer than ${minimumRemaining} minutes remain.`;
      }
      return "Drop-in isn't open right now — there's no active window at this facility.";
    }
    if (err.code === "INVALID_PAYMENT_METHOD") {
      return "Payment method required — add a payment method and try again.";
    }
    if (err.code === "DROPIN_PRICE_CHANGED") {
      return "The drop-in price changed. Review the latest price and try again.";
    }
    if (err.code === "DROPIN_POLICY_CHANGED") {
      return "The cancellation terms changed. Review the latest terms and try again.";
    }
    if (err.code === "DROPIN_WAIVER_CHANGED_AFTER_PAYMENT") {
      return "Your payment was reversed because the facility waiver changed. Accept the current waiver, then start a new check-in.";
    }
    if (err.code === "FACILITY_PAYMENTS_UNAVAILABLE") {
      return "This facility can't take payments right now. Please ask staff or try again later.";
    }
    if (err.code === "DROPIN_PAYMENT_UNCERTAIN") {
      const correlationId = err.details?.correlation_id;
      const reference =
        typeof correlationId === "string" && correlationId.trim()
          ? ` Reference: ${correlationId}.`
          : "";
      return `We couldn't confirm your payment. A retry will use the same payment details. If it still can't confirm, ask facility staff to check it.${reference}`;
    }
    if (err.status === 409) {
      return "Drop-in isn't open right now — there's no active window at this facility.";
    }
  }
  return "We couldn't check you in. Please try again.";
}
function isCurrentCheckoutSession(
  session: DropInSessionResponse | null | undefined,
  facilityId: string,
  userId: string | null,
): session is DropInSessionResponse {
  return (
    userId !== null &&
    session !== null &&
    session !== undefined &&
    session.facility_id === facilityId &&
    session.user_id === userId &&
    (session.status === "active" || session.status === "pending_validation")
  );
}

/**
 * Build the check-in REQUEST body, carrying the identifier the backend REQUIRES
 * for the chosen method (#1213):
 *   • membership / package → `subscription_id` (the selected option's id)
 *   • single               → `stripe_payment_method_id` (the picked saved card)
 * Returns null when the selection is incomplete (no method, or single with no
 * card chosen) — the page gates the CTA on a non-null body, so a method is
 * NEVER submitted without its required id (the 422 guard).
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;
type CheckInBody = DistributiveOmit<DropInCheckInRequest, "idempotency_key">;

function buildCheckInRequest(
  selection: PaymentSelection | null,
  cardId: string | null,
  singleRequiresPaymentMethod: boolean,
  priceQuoteVersion: string | null,
  checkoutTerms: DropInPaymentOption["checkout_terms"] | undefined,
  campaignId: string | null,
): CheckInBody | null {
  if (!selection || !checkoutTerms) return null;
  const cancellationPolicy = {
    cancellation_policy_code: checkoutTerms.policy_code,
    cancellation_policy_id: checkoutTerms.policy_id,
    cancellation_policy_version: checkoutTerms.policy_version,
    checkout_activation_mode: checkoutTerms.checkout_activation_mode,
  } as const;
  if (selection.method === "single") {
    if (!priceQuoteVersion) return null;
    if (!singleRequiresPaymentMethod) {
      return {
        ...cancellationPolicy,
        payment_method: "single",
        price_quote_version: priceQuoteVersion,
        ...(campaignId ? { campaign_id: campaignId } : {}),
      };
    }
    if (!cardId) return null;
    return {
      ...cancellationPolicy,
      payment_method: "single",
      stripe_payment_method_id: cardId,
      price_quote_version: priceQuoteVersion,
      ...(campaignId ? { campaign_id: campaignId } : {}),
    };
  }
  // membership | package — both identified by their subscription_id.
  return {
    ...cancellationPolicy,
    payment_method: selection.method,
    subscription_id: selection.subscriptionId,
  };
}

type CheckInAttempt = {
  userId: string;
  idempotencyKey: string;
  body: CheckInBody;
  operationToken?: string;
};

const ATTEMPT_STORAGE_PREFIX = "bw:dropin-checkin-attempt:";

function attemptStorageKey(facilityId: string): string {
  return `${ATTEMPT_STORAGE_PREFIX}${facilityId}`;
}

function parseCheckInAttempt(raw: string): CheckInAttempt | null {
  const parsed = JSON.parse(raw) as {
    userId?: unknown;
    idempotencyKey?: unknown;
    operationToken?: unknown;
    body?: Record<string, unknown>;
  };
  const method = parsed.body?.payment_method;
  if (
    typeof parsed.userId !== "string" ||
    !parsed.userId ||
    typeof parsed.idempotencyKey !== "string" ||
    !parsed.idempotencyKey ||
    (parsed.operationToken !== undefined &&
      (typeof parsed.operationToken !== "string" || !parsed.operationToken)) ||
    (method !== "single" && method !== "package" && method !== "membership")
  ) {
    return null;
  }

  const stripePaymentMethodId = parsed.body?.stripe_payment_method_id;
  const subscriptionId = parsed.body?.subscription_id;
  const priceQuoteVersion = parsed.body?.price_quote_version;
  const cancellationPolicyCode = parsed.body?.cancellation_policy_code;
  const cancellationPolicyId = parsed.body?.cancellation_policy_id;
  const cancellationPolicyVersion = parsed.body?.cancellation_policy_version;
  const checkoutActivationMode = parsed.body?.checkout_activation_mode;
  const campaignId = parsed.body?.campaign_id;
  if (
    (stripePaymentMethodId !== undefined &&
      stripePaymentMethodId !== null &&
      typeof stripePaymentMethodId !== "string") ||
    (subscriptionId !== undefined &&
      subscriptionId !== null &&
      typeof subscriptionId !== "string") ||
    (priceQuoteVersion !== undefined &&
      priceQuoteVersion !== null &&
      typeof priceQuoteVersion !== "string") ||
    cancellationPolicyCode !== "open_play_cancellation" ||
    typeof cancellationPolicyId !== "string" ||
    cancellationPolicyId.trim().length === 0 ||
    typeof cancellationPolicyVersion !== "number" ||
    !Number.isInteger(cancellationPolicyVersion) ||
    cancellationPolicyVersion < 1 ||
    (checkoutActivationMode !== "immediate" &&
      checkoutActivationMode !== "staff_validation") ||
    (campaignId !== undefined &&
      campaignId !== null &&
      typeof campaignId !== "string")
  ) {
    return null;
  }

  const cancellationPolicy = {
    cancellation_policy_code: cancellationPolicyCode,
    cancellation_policy_id: cancellationPolicyId,
    cancellation_policy_version: cancellationPolicyVersion,
    checkout_activation_mode: checkoutActivationMode,
  } as const;
  let body: CheckInBody;
  if (method === "single") {
    if (
      typeof priceQuoteVersion !== "string" ||
      !/^[0-9a-f]{64}$/.test(priceQuoteVersion)
    ) {
      return null;
    }
    body = {
      ...cancellationPolicy,
      payment_method: "single",
      price_quote_version: priceQuoteVersion,
      ...(typeof stripePaymentMethodId === "string"
        ? { stripe_payment_method_id: stripePaymentMethodId }
        : {}),
      ...(typeof campaignId === "string" ? { campaign_id: campaignId } : {}),
    };
  } else {
    if (typeof subscriptionId !== "string") return null;
    body = {
      ...cancellationPolicy,
      payment_method: method,
      subscription_id: subscriptionId,
    };
  }

  return {
    userId: parsed.userId,
    idempotencyKey: parsed.idempotencyKey,
    body,
    ...(typeof parsed.operationToken === "string"
      ? { operationToken: parsed.operationToken }
      : {}),
  };
}

function loadCheckInAttempt(
  facilityId: string,
  userId: string | null,
): CheckInAttempt | null {
  if (typeof window === "undefined") return null;
  const storageKey = attemptStorageKey(facilityId);
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = parseCheckInAttempt(raw);
    if (!parsed || parsed.userId !== userId) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return null;
  }
}

function purgeCheckInAttemptsForOtherUsers(userId: string | null): void {
  if (typeof window === "undefined") return;
  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index);
    if (!key?.startsWith(ATTEMPT_STORAGE_PREFIX)) continue;
    if (!userId) {
      window.sessionStorage.removeItem(key);
      continue;
    }
    try {
      const raw = window.sessionStorage.getItem(key);
      const attempt = raw ? parseCheckInAttempt(raw) : null;
      if (!attempt || attempt.userId !== userId) {
        window.sessionStorage.removeItem(key);
      }
    } catch {
      window.sessionStorage.removeItem(key);
    }
  }
}

function removeStoredCheckInAttempt(
  facilityId: string,
  attempt: CheckInAttempt,
): void {
  const storageKey = attemptStorageKey(facilityId);
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    const stored = raw ? parseCheckInAttempt(raw) : null;
    if (
      stored?.userId === attempt.userId &&
      stored.idempotencyKey === attempt.idempotencyKey &&
      stored.operationToken === attempt.operationToken
    ) {
      window.sessionStorage.removeItem(storageKey);
    }
  } catch {
    window.sessionStorage.removeItem(storageKey);
  }
}

function selectionFromAttempt(
  attempt: CheckInAttempt | null,
): PaymentSelection | null {
  if (!attempt) return null;
  const { body } = attempt;
  if (body.payment_method === "single") return { method: "single" };
  if (!body.subscription_id) return null;
  return {
    method: body.payment_method,
    subscriptionId: body.subscription_id,
  };
}

function isSelectionAvailable(
  selection: PaymentSelection | null,
  options: DropInPaymentOption | undefined,
): boolean {
  if (!selection || !options) return false;
  if (selection.method === "single") {
    return (
      options.single_visit_available &&
      options.single_price !== null &&
      options.single_price !== undefined
    );
  }
  const candidates =
    selection.method === "membership"
      ? options.active_memberships
      : options.active_packages;
  return (candidates ?? []).some(
    (candidate) => candidate.subscription_id === selection.subscriptionId,
  );
}

function isOnlyPaidCardOption(
  options: DropInPaymentOption | undefined,
): boolean {
  return Boolean(
    options?.active_now &&
      options.single_visit_available &&
      options.single_requires_payment_method &&
      options.single_price !== null &&
      options.single_price !== undefined &&
      options.active_memberships.length === 0 &&
      options.active_packages.length === 0,
  );
}

function isAttemptAvailable(
  attempt: CheckInAttempt,
  options: DropInPaymentOption | undefined,
): boolean {
  if (!options?.active_now) return false;
  if (!isSelectionAvailable(selectionFromAttempt(attempt), options)) {
    return false;
  }
  if (attempt.body.payment_method !== "single" || !options) return true;
  return (
    attempt.body.price_quote_version === options.price_quote_version &&
    Boolean(attempt.body.stripe_payment_method_id) ===
      options.single_requires_payment_method
  );
}

function withAdmissionUnavailable(
  options: DropInPaymentOption,
): DropInPaymentOption {
  if (options.admission_unavailable_reason === "closing_soon") {
    return options;
  }
  return { ...options, active_now: false };
}

function withCardPaymentsUnavailable(
  options: DropInPaymentOption,
): DropInPaymentOption {
  if (options.admission_unavailable_reason === "closing_soon") {
    return {
      ...options,
      single_visit_available: false,
      single_unavailable_reason: "card_payments_not_ready",
    };
  }
  return {
    ...options,
    single_visit_available: false,
    single_unavailable_reason: "card_payments_not_ready",
  };
}

/**
 * Drop-in CHECK-IN (/dropin/:facilityId).
 *
 * The CURRENT user's own individual check-in: pick a payment method (driven by
 * GET drop-in/payment-options), accept your OWN per-person waiver if the
 * facility requires one, then POST the exact price and cancellation-policy
 * versions shown at review. The backend revalidates both plus the waiver before
 * admission. On success the session renders the backend-owned expiry in the
 * facility timezone.
 *
 * Payment wiring (#1213): membership/package send `subscription_id`; the single
 * drop-in fee surfaces the shared facility-wallet card spine (saved-card picker
 * + add-a-card via SetupIntent, scoped to the facility's connected account) and
 * sends the chosen `stripe_payment_method_id`. The CTA stays gated until the
 * selection carries its required identifier, so no method can submit without it.
 *
 * No delegation: each friend checks in on their own device.
 */
export function DropInCheckInPage() {
  const { facilityId = "" } = useParams<{ facilityId: string }>();
  const { user, loading: authLoading } = useAuth();
  // RequireProfile owns the server read and keeps this cache entry active for
  // every guarded Player route. Observe that canonical value without starting
  // a second request from checkout or falling back to stale auth-provider copy.
  const { data: profile } = useMyProfile(false);
  const userId = user?.uid ?? null;
  const playerName =
    profile?.user_id === userId
      ? profile.display_name?.trim() || "Player profile unavailable"
      : "Player profile unavailable";

  useEffect(() => {
    if (authLoading) return;
    purgeCheckInAttemptsForOtherUsers(userId);
  }, [authLoading, userId]);

  if (authLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary" role="status">
          Loading…
        </Typography>
      </Box>
    );
  }

  // Facility and authenticated user jointly scope every mutation and local form
  // state. A user change remounts before a persisted attempt can be retried.
  return (
    <DropInCheckInFacilityPage
      key={`${facilityId}:${userId ?? "signed-out"}`}
      facilityId={facilityId}
      userId={userId}
      playerName={playerName}
    />
  );
}

type CheckoutStep = "payment" | "review";

function subscribeToOnlineStatus(onStoreChange: () => void) {
  return onlineManager.subscribe(onStoreChange);
}

function getOnlineStatus() {
  return onlineManager.isOnline();
}

function DropInCheckInFacilityPage({
  facilityId,
  userId,
  playerName,
}: {
  facilityId: string;
  userId: string | null;
  playerName: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const walletQueryKey = paymentQueryKeys.paymentMethods(facilityId);
  const isOnline = useSyncExternalStore(
    subscribeToOnlineStatus,
    getOnlineStatus,
    () => true,
  );
  const waiver = useWaiverGate(facilityId);
  const paymentOptions = useDropInPaymentOptions(facilityId);
  // The revision stream is safe before admission, but the presence-bearing
  // court board is not. Checkout only reconciles actor/payment reads.
  const courtState = useDropInCourtState(facilityId, 0, {
    reconcileBoard: false,
  });
  const sessionQuery = useDropInSession(facilityId);
  const checkIn = useCheckIn(facilityId);
  const resetCheckIn = checkIn.reset;
  // Persist the immutable key + payload across timeout retries and reloads.
  // A different payload must never reuse an operation that may have charged.
  const [checkInAttempt, setCheckInAttempt] = useState<CheckInAttempt | null>(
    () => loadCheckInAttempt(facilityId, userId),
  );
  const [paymentAuthentication, setPaymentAuthentication] = useState<{
    clientSecret: string;
    attempt: CheckInAttempt;
    operationId: number;
  } | null>(null);
  const operationGeneration = useRef(0);
  const paymentHeadingRef = useRef<HTMLHeadingElement>(null);
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const reviewDisabledReasonId = useId();
  const finalActionDisabledReasonId = useId();
  const savedCardsRef = useRef<HTMLDivElement>(null);
  const walletRetryRegionRef = useRef<HTMLDivElement>(null);
  const useSavedCardAfterRetryRef = useRef<HTMLButtonElement>(null);
  const newCardEntryRef = useRef<HTMLDivElement>(null);
  const paymentReviewActionRef = useRef<HTMLButtonElement>(null);
  const paymentFocusTargetRef = useRef<HTMLElement | null>(null);
  const restorePaymentFocusRef = useRef(false);
  const finalActionRef = useRef<HTMLButtonElement>(null);
  const walletRetryFocusRequestedRef = useRef(false);
  const finalActionFocusedRef = useRef(false);
  const restoreFinalActionFocusRef = useRef(false);
  const freshCardFinalActionBlockedRef = useRef(false);
  const walletValidationRequiredRef = useRef(!onlineManager.isOnline());
  const walletValidationEpochRef = useRef(onlineManager.isOnline() ? 0 : 1);
  const walletValidationAttemptedEpochRef = useRef(0);
  const [walletValidationEpoch, setWalletValidationEpoch] = useState(
    () => walletValidationEpochRef.current,
  );
  const [walletValidatedEpoch, setWalletValidatedEpoch] = useState(0);
  const walletValidationRequired = walletValidatedEpoch < walletValidationEpoch;
  const captureWalletValidationEpoch = useCallback(() => {
    if (!walletValidationRequiredRef.current) return null;
    const epoch = walletValidationEpochRef.current;
    walletValidationAttemptedEpochRef.current = Math.max(
      walletValidationAttemptedEpochRef.current,
      epoch,
    );
    return epoch;
  }, []);
  const markWalletValidationSuccessful = useCallback((epoch: number | null) => {
    if (epoch === null || walletValidationEpochRef.current !== epoch) return;
    walletValidationRequiredRef.current = false;
    setWalletValidatedEpoch((current) => Math.max(current, epoch));
  }, []);
  const focusStepAfterRender = useRef(false);
  const focusSavedCardAfterRender = useRef(false);
  const focusNewCardOnMount = useRef(false);
  const checkInFlight = useRef(false);
  useEffect(
    () => () => {
      operationGeneration.current += 1;
    },
    [],
  );
  // The facility's queue mode decides the post-check-in path — a self_serve
  // facility sends the player to the court board (pick a court), auto/manual to
  // the queue. The CTA copy branches so the promise matches what actually happens.
  const facilityMode = useFacilityMode(facilityId);
  const isSelfServe = facilityMode.data?.queue_assignment_mode === "self_serve";

  // The chosen payment method (+ its subscription id for membership/package).
  // Multiple choices start unselected. A sole paid-card option is authoritative
  // and needs no redundant player decision, so it is selected below.
  const [selectionState, setSelectionState] = useState<{
    facilityId: string;
    value: PaymentSelection | null;
  }>({ facilityId, value: selectionFromAttempt(checkInAttempt) });
  const storedSelection =
    selectionState.facilityId === facilityId ? selectionState.value : null;
  const [cardPaymentsRejected, setCardPaymentsRejected] = useState(false);
  const [admissionRejected, setAdmissionRejected] = useState(false);
  const admissionRecoveryPhase = useRef<
    | "idle"
    | "awaiting_revision"
    | "awaiting_pending_clear"
    | "awaiting_completion"
  >("idle");
  // React Query retains successful data when a background refetch fails. Treat
  // that retained payment data as unavailable so a stale price/method can never
  // remain armed behind the visible error state.
  const loadedPaymentOptions =
    paymentOptions.isError || !paymentOptions.data
      ? undefined
      : canonicalizeDropInPaymentOptions(paymentOptions.data);
  const availabilityPaymentOptions =
    admissionRejected && loadedPaymentOptions
      ? withAdmissionUnavailable(loadedPaymentOptions)
      : loadedPaymentOptions;
  useEffect(() => {
    if (!admissionRejected) {
      admissionRecoveryPhase.current = "idle";
      return;
    }
    if (admissionRecoveryPhase.current === "awaiting_pending_clear") {
      if (!courtState.isActorRevisionPending) {
        admissionRecoveryPhase.current = "awaiting_revision";
      }
      return;
    }
    if (admissionRecoveryPhase.current === "awaiting_revision") {
      if (courtState.isActorRevisionPending) {
        admissionRecoveryPhase.current = "awaiting_completion";
      }
      return;
    }
    if (
      admissionRecoveryPhase.current === "awaiting_completion" &&
      !courtState.isActorRevisionPending
    ) {
      admissionRecoveryPhase.current = "awaiting_revision";
      if (loadedPaymentOptions?.active_now === true) {
        resetCheckIn();
        setAdmissionRejected(false);
      }
    }
  }, [
    admissionRejected,
    courtState.isActorRevisionPending,
    loadedPaymentOptions?.active_now,
    resetCheckIn,
  ]);
  const currentPaymentOptions =
    cardPaymentsRejected && availabilityPaymentOptions
      ? withCardPaymentsUnavailable(availabilityPaymentOptions)
      : availabilityPaymentOptions;
  const paymentOptionsReady =
    currentPaymentOptions !== undefined && !courtState.isActorRevisionPending;
  const explicitSelection = isSelectionAvailable(
    storedSelection,
    currentPaymentOptions,
  )
    ? storedSelection
    : null;
  const automaticSingleSelection: PaymentSelection | null =
    explicitSelection === null && isOnlyPaidCardOption(currentPaymentOptions)
      ? { method: "single" }
      : null;
  const selection = explicitSelection ?? automaticSingleSelection;
  // The saved card chosen for the SINGLE drop-in fee path (the shared picker).
  const [selectedPmState, setSelectedPmState] = useState<{
    facilityId: string;
    value: string | null;
  }>({
    facilityId,
    value: checkInAttempt?.body.stripe_payment_method_id ?? null,
  });
  const selectedPmId =
    selectedPmState.facilityId === facilityId ? selectedPmState.value : null;
  const setSelectedPmId = (value: string | null) => {
    setSelectedPmState({ facilityId, value });
  };
  // Whether the add-a-new-card form (Stripe Elements) is revealed. Collapsed
  // behind a button so a player with saved cards isn't shown a raw card field
  // until they choose to add one — and a player with NO saved card always has a
  // reachable way to pay.
  const [addingCardState, setAddingCardState] = useState({
    facilityId,
    value: false,
  });
  const addingCard =
    addingCardState.facilityId === facilityId && addingCardState.value;
  const setAddingCard = (value: boolean) => {
    setAddingCardState({ facilityId, value });
  };
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>(() =>
    checkInAttempt ? "review" : "payment",
  );
  const [cardReconcileError, setCardReconcileError] = useState<string | null>(
    null,
  );
  const [pendingCardPmId, setPendingCardPmId] = useState<string | null>(null);
  const [cardSavePending, setCardSavePending] = useState(false);
  const [walletRetryFacilityId, setWalletRetryFacilityId] = useState<
    string | null
  >(null);
  const [walletRecoveryFacilityId, setWalletRecoveryFacilityId] = useState<
    string | null
  >(null);

  const goToStep = (nextStep: CheckoutStep) => {
    focusStepAfterRender.current = true;
    setCheckoutStep(nextStep);
  };

  useEffect(() => {
    if (!focusStepAfterRender.current) return;
    focusStepAfterRender.current = false;
    (checkoutStep === "review"
      ? reviewHeadingRef.current
      : paymentHeadingRef.current
    )?.focus();
  }, [checkoutStep]);

  useEffect(() => {
    if (!focusSavedCardAfterRender.current || addingCard) return;
    focusSavedCardAfterRender.current = false;
    const savedCards = savedCardsRef.current;
    (
      savedCards?.querySelector<HTMLInputElement>(
        'input[type="radio"]:checked',
      ) ??
      savedCards?.querySelector<HTMLInputElement>('input[type="radio"]') ??
      savedCards
    )?.focus();
  }, [addingCard]);

  const setSelection = (value: PaymentSelection) => {
    setSelectionState({ facilityId, value });
    setCardReconcileError(null);
    setPendingCardPmId(null);
    if (value.method !== "single") {
      focusNewCardOnMount.current = false;
      setAddingCard(false);
    }
  };

  const isSingle = selection?.method === "single";
  const singleRequiresPaymentMethod =
    currentPaymentOptions?.single_requires_payment_method !== false;
  const needsSingleCard = isSingle && singleRequiresPaymentMethod;
  // The facility-wallet card spine only loads on the single path (lazy — the
  // membership/package paths never touch a card). Behaviour mirrors booking's
  // ReviewPage: config + saved cards gated on the step that needs them.
  const configQ = useFrontendConfig(facilityId, needsSingleCard);
  const methodsQ = usePaymentMethods(facilityId, needsSingleCard, {
    captureRequestEpoch: captureWalletValidationEpoch,
    onRequestSuccess: markWalletValidationSuccessful,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
  const setupIntent = useWalletSetupIntent();
  const methods = methodsQ.data ?? [];
  const walletReadPending = needsSingleCard && methodsQ.fetchStatus !== "idle";
  const paymentMethodsUnavailable =
    needsSingleCard && methodsQ.isError && pendingCardPmId === null;
  const walletErrorAllowsNewCard =
    paymentMethodsUnavailable && configQ.data !== undefined;
  const walletRetryPending =
    needsSingleCard && walletRetryFacilityId === facilityId;
  const walletRecoveryActive =
    needsSingleCard && walletRecoveryFacilityId === facilityId;
  const walletRefetchPending =
    walletRetryPending || (walletRecoveryActive && walletReadPending);
  const cardEntryOpen =
    needsSingleCard && (addingCard || walletErrorAllowsNewCard);
  const showWalletReadError = paymentMethodsUnavailable || walletRefetchPending;
  const observedWalletRef = useRef<{
    facilityId: string;
    data: SavedPaymentMethodResponse[];
  } | null>(null);
  const requireFreshWalletValidation = useCallback(() => {
    const activeElement = document.activeElement;
    if (
      checkoutStep === "payment" &&
      checkInAttempt === null &&
      needsSingleCard &&
      activeElement instanceof HTMLElement &&
      (activeElement === paymentReviewActionRef.current ||
        savedCardsRef.current?.contains(activeElement))
    ) {
      paymentFocusTargetRef.current = activeElement;
      restorePaymentFocusRef.current = true;
    }

    const nextEpoch = walletValidationEpochRef.current + 1;
    walletValidationEpochRef.current = nextEpoch;
    walletValidationRequiredRef.current = true;
    setWalletValidationEpoch(nextEpoch);
  }, [checkInAttempt, checkoutStep, needsSingleCard]);

  useEffect(
    () =>
      onlineManager.subscribe((online) => {
        const resumesOwnedValidation =
          online &&
          walletValidationRequiredRef.current &&
          walletValidationAttemptedEpochRef.current ===
            walletValidationEpochRef.current;
        if (!resumesOwnedValidation) requireFreshWalletValidation();
      }),
    [requireFreshWalletValidation],
  );

  useEffect(
    () =>
      focusManager.subscribe((focused) => {
        if (focused && onlineManager.isOnline()) {
          requireFreshWalletValidation();
        }
      }),
    [requireFreshWalletValidation],
  );

  useEffect(() => {
    if (
      !walletValidationRequired ||
      checkInAttempt !== null ||
      !needsSingleCard
    ) {
      return;
    }
    const epoch = walletValidationEpoch;
    if (
      methodsQ.fetchStatus !== "idle" ||
      walletValidationAttemptedEpochRef.current >= epoch
    ) {
      return;
    }
    walletValidationAttemptedEpochRef.current = epoch;
    void methodsQ.refetch({ cancelRefetch: true });
  }, [
    checkInAttempt,
    isOnline,
    methodsQ,
    needsSingleCard,
    walletValidationEpoch,
    walletValidationRequired,
  ]);

  useEffect(() => {
    if (
      !needsSingleCard ||
      !methodsQ.isSuccess ||
      methodsQ.isFetching ||
      methodsQ.data === undefined ||
      (observedWalletRef.current?.facilityId === facilityId &&
        observedWalletRef.current.data === methodsQ.data)
    ) {
      return;
    }
    const isFirstListForFacility =
      observedWalletRef.current?.facilityId !== facilityId;
    observedWalletRef.current = { facilityId, data: methodsQ.data };
    if (isFirstListForFacility && selectedPmId === null) {
      const preferred = methods.find(
        (method) => method.is_default && method.is_usable,
      );
      if (preferred) {
        setSelectedPmId(preferred.payment_method_id);
      }
    }
  }, [
    facilityId,
    methods,
    methodsQ.data,
    methodsQ.isFetching,
    methodsQ.isSuccess,
    needsSingleCard,
    selectedPmId,
  ]);

  useEffect(() => {
    if (
      !needsSingleCard ||
      selectedPmId === null ||
      !methodsQ.isSuccess ||
      methodsQ.isFetching ||
      methods.some(
        (method) =>
          method.payment_method_id === selectedPmId && method.is_usable,
      )
    ) {
      return;
    }
    setSelectedPmId(null);
  }, [
    methods,
    methodsQ.isFetching,
    methodsQ.isSuccess,
    needsSingleCard,
    selectedPmId,
  ]);

  useEffect(() => {
    if (
      checkoutStep === "payment" &&
      needsSingleCard &&
      methodsQ.isSuccess &&
      !methodsQ.isFetching &&
      methods.length === 0 &&
      selectedPmId === null
    ) {
      setAddingCard(true);
    }
  }, [
    checkoutStep,
    methods.length,
    methodsQ.isFetching,
    methodsQ.isSuccess,
    needsSingleCard,
    selectedPmId,
  ]);

  useEffect(() => {
    if (
      checkoutStep !== "payment" ||
      !needsSingleCard ||
      !methodsQ.isError ||
      configQ.data === undefined
    ) {
      return;
    }
    setAddingCardState((current) =>
      current.facilityId === facilityId && current.value
        ? current
        : { facilityId, value: true },
    );
  }, [
    checkoutStep,
    configQ.data,
    facilityId,
    methodsQ.isError,
    needsSingleCard,
  ]);

  useEffect(() => {
    if (needsSingleCard && methodsQ.isError && pendingCardPmId === null) {
      setWalletRecoveryFacilityId((current) =>
        current === facilityId ? current : facilityId,
      );
      return;
    }
    if (
      walletRecoveryFacilityId === facilityId &&
      methodsQ.isSuccess &&
      methodsQ.fetchStatus === "idle"
    ) {
      setWalletRecoveryFacilityId(null);
    }
  }, [
    facilityId,
    methodsQ.isError,
    methodsQ.isSuccess,
    methodsQ.fetchStatus,
    needsSingleCard,
    pendingCardPmId,
    walletRecoveryFacilityId,
  ]);

  useEffect(() => {
    if (
      !walletRecoveryActive ||
      !walletRefetchPending ||
      walletRetryFocusRequestedRef.current
    ) {
      return;
    }
    if (walletRetryRegionRef.current?.contains(document.activeElement)) {
      walletRetryFocusRequestedRef.current = true;
    }
  }, [walletRecoveryActive, walletRefetchPending]);

  useEffect(() => {
    if (!walletRetryFocusRequestedRef.current || walletRefetchPending) {
      return;
    }
    walletRetryFocusRequestedRef.current = false;

    const activeElement = document.activeElement;
    if (
      activeElement &&
      activeElement !== document.body &&
      activeElement !== document.documentElement
    ) {
      return;
    }

    if (methodsQ.isSuccess) {
      (useSavedCardAfterRetryRef.current ?? newCardEntryRef.current)?.focus();
      return;
    }
    if (methodsQ.isError) {
      walletRetryRegionRef.current
        ?.querySelector<HTMLButtonElement>("button")
        ?.focus();
    }
  }, [methodsQ.isError, methodsQ.isSuccess, walletRefetchPending]);

  const createSetupIntentSecret = useCallback(
    async (idempotencyKey: string) => {
      const res = await setupIntent.mutateAsync(idempotencyKey);
      return res.client_secret;
    },
    [setupIntent],
  );
  const newCardForm = configQ.data ? (
    <MaterialConnectedElementsProvider config={configQ.data}>
      <MaterialAddCardForm
        publishableKey={configQ.data.publishable_key}
        autoFocus={focusNewCardOnMount.current}
        createSetupIntent={createSetupIntentSecret}
        onBusyChange={setCardSavePending}
        onCardAdded={reconcileAddedCard}
      />
    </MaterialConnectedElementsProvider>
  ) : null;

  function handleWalletRetryFocus() {
    walletRetryFocusRequestedRef.current = true;
  }

  function handleWalletRetryBlur(event: FocusEvent<HTMLDivElement>) {
    const nextTarget = event.relatedTarget;
    if (
      nextTarget instanceof Node &&
      !event.currentTarget.contains(nextTarget)
    ) {
      walletRetryFocusRequestedRef.current = false;
    }
  }

  function handleFinalActionFocus() {
    finalActionFocusedRef.current = true;
  }

  function handleFinalActionBlur(event: FocusEvent<HTMLButtonElement>) {
    const nextTarget = event.relatedTarget;
    if (
      freshCardFinalActionBlockedRef.current &&
      (nextTarget === null ||
        nextTarget === document.body ||
        nextTarget === document.documentElement)
    ) {
      restoreFinalActionFocusRef.current = true;
      return;
    }
    finalActionFocusedRef.current = false;
    restoreFinalActionFocusRef.current = false;
  }

  async function retryPaymentMethods() {
    walletRetryFocusRequestedRef.current =
      walletRetryRegionRef.current?.contains(document.activeElement) === true;
    setWalletRetryFacilityId(facilityId);
    try {
      await methodsQ.refetch({ cancelRefetch: true });
    } finally {
      setWalletRetryFacilityId((current) =>
        current === facilityId ? null : current,
      );
    }
  }

  const existingSession = sessionQuery.data ?? null;
  const justCheckedIn = isCurrentCheckoutSession(
    checkIn.data,
    facilityId,
    userId,
  )
    ? checkIn.data
    : null;
  const reconciledSession =
    sessionQuery.isSuccess &&
    sessionQuery.isFetchedAfterMount &&
    !sessionQuery.isFetching &&
    sessionQuery.error === null &&
    isCurrentCheckoutSession(existingSession, facilityId, userId)
      ? existingSession
      : null;
  const activeSession = justCheckedIn ?? reconciledSession;

  useEffect(() => {
    if (!activeSession) return;
    if (checkInAttempt) {
      removeStoredCheckInAttempt(facilityId, checkInAttempt);
      setCheckInAttempt(null);
    }
    navigate(`/dropin/${facilityId}/session`, { replace: true });
  }, [activeSession, checkInAttempt, facilityId, navigate]);

  // Fresh Drop-in attempts are only checkable-in when an open_play rule matches
  // right now (Slice 1, unified pricing). An already-persisted attempt remains
  // replayable so the backend can reconcile or compensate a payment that may
  // already have completed.
  const isOpenNow =
    !courtState.isActorRevisionPending &&
    (currentPaymentOptions?.active_now ?? false);
  const admissionClosedMessage = currentPaymentOptions
    ? admissionUnavailableMessage(currentPaymentOptions)
    : "Drop-in is not open right now.";

  // "This facility never configured drop-in pricing" — the AUTHORITATIVE signal
  // is the backend's has_dropin_price flag, which distinguishes a never-priced
  // facility from a priced-but-merely-closed one (both carry a null
  // single_price, so single_price alone is ambiguous). Surfaced PRE-EMPTIVELY
  // so a player at an un-priced facility gets the exact "hasn't set a drop-in
  // price yet" copy — the original Synergy empty-payment-screen bug — rather
  // than the generic "not running right now". The 422 NO_DROPIN_PRICE on a
  // check-in POST is the same signal for the case a player still submits.
  const opts = currentPaymentOptions;
  const facilityHasNoDropInPrice = opts?.has_dropin_price === false;

  const noPriceFromError =
    checkIn.isError &&
    isApiError(checkIn.error) &&
    checkIn.error.code === "NO_DROPIN_PRICE";
  const waiverFailure =
    checkIn.isError &&
    isApiError(checkIn.error) &&
    checkIn.error.status === 428;
  const compensatedWaiverFailure =
    waiverFailure &&
    isApiError(checkIn.error) &&
    checkIn.error.code === "DROPIN_WAIVER_CHANGED_AFTER_PAYMENT";
  const showNoPrice = noPriceFromError || facilityHasNoDropInPrice;

  // ---- error mapping (honest inline states) -------------------------------
  // The dedicated no-price block owns the NO_DROPIN_PRICE case, so the generic
  // inline error is suppressed for it (no duplicate copy).
  const checkInError =
    checkIn.isError &&
    !noPriceFromError &&
    (!waiverFailure || compensatedWaiverFailure) &&
    !paymentAuthentication
      ? mapCheckInError(checkIn.error)
      : null;

  const selectedCard =
    selectedPmId === null
      ? null
      : (methods.find((method) => method.payment_method_id === selectedPmId) ??
        null);
  const chargeReadySelectedPmId =
    !needsSingleCard ||
    (methodsQ.isSuccess &&
      methodsQ.fetchStatus === "idle" &&
      selectedCard?.is_usable === true)
      ? selectedPmId
      : null;

  useEffect(() => {
    if (
      checkoutStep !== "review" ||
      checkInAttempt !== null ||
      !needsSingleCard ||
      methodsQ.fetchStatus !== "idle" ||
      (methodsQ.isSuccess && selectedCard?.is_usable === true)
    ) {
      return;
    }
    focusStepAfterRender.current = true;
    setCheckoutStep("payment");
  }, [
    checkInAttempt,
    checkoutStep,
    methodsQ.fetchStatus,
    methodsQ.isSuccess,
    needsSingleCard,
    selectedCard,
  ]);

  // The request the CTA would submit — null when the selection is incomplete
  // (no method chosen, or single without a card). The CTA is gated on this.
  const checkInBody = buildCheckInRequest(
    selection,
    chargeReadySelectedPmId,
    singleRequiresPaymentMethod,
    currentPaymentOptions?.price_quote_version ?? null,
    currentPaymentOptions?.checkout_terms,
    currentPaymentOptions?.campaign?.campaign_id ?? null,
  );
  const submissionBody = checkInAttempt?.body ?? checkInBody;
  const hasImmutableRecoveryAttempt = checkInAttempt !== null;
  const recoveringUnavailableAttempt =
    hasImmutableRecoveryAttempt &&
    !isAttemptAvailable(checkInAttempt, currentPaymentOptions);
  const paymentSelectionLocked =
    checkIn.isPending ||
    checkInAttempt !== null ||
    cardSavePending ||
    (pendingCardPmId !== null && cardReconcileError === null);
  const cardSelectionLocked = paymentSelectionLocked || walletReadPending;
  const freshCardWalletReadPending =
    checkInAttempt === null && walletReadPending;
  const freshCardOffline =
    checkInAttempt === null && needsSingleCard && !isOnline;
  const freshCardWalletValidationPending =
    checkInAttempt === null && needsSingleCard && walletValidationRequired;
  const freshCardFinalActionBlocked =
    freshCardWalletReadPending ||
    freshCardOffline ||
    freshCardWalletValidationPending;
  freshCardFinalActionBlockedRef.current = freshCardFinalActionBlocked;

  const ctaDisabled =
    !userId ||
    (!hasImmutableRecoveryAttempt && !paymentOptionsReady) ||
    ((!hasImmutableRecoveryAttempt || waiverFailure) && waiver.isBlocked) ||
    checkIn.isPending ||
    freshCardWalletReadPending ||
    freshCardOffline ||
    freshCardWalletValidationPending ||
    submissionBody === null ||
    (!hasImmutableRecoveryAttempt && !isOpenNow);
  const reviewDisabled =
    checkInBody === null ||
    paymentSelectionLocked ||
    walletReadPending ||
    (needsSingleCard && walletValidationRequired) ||
    !isOpenNow;
  const reviewDisabledReason = !reviewDisabled
    ? null
    : !isOpenNow
      ? "Drop-in is not open right now."
      : needsSingleCard && (walletReadPending || walletValidationRequired)
        ? "Payment details are updating. Wait to continue."
        : needsSingleCard && paymentMethodsUnavailable
          ? "Saved cards could not be refreshed. Retry to continue."
          : paymentSelectionLocked
            ? "Payment details are updating. Wait to continue."
            : selection === null
              ? "Choose a payment method to continue."
              : needsSingleCard && chargeReadySelectedPmId === null
                ? "Choose a saved card or add a new card to continue."
                : "Complete your payment details to continue.";
  const finalActionDisabledReason =
    checkoutStep === "review" && !hasImmutableRecoveryAttempt && !isOpenNow
      ? admissionClosedMessage
      : checkoutStep === "review" &&
          checkInAttempt === null &&
          needsSingleCard &&
          ctaDisabled
        ? freshCardOffline
          ? "Connect to the internet before checking in with a saved card."
          : walletReadPending || walletValidationRequired
            ? "Payment details are updating. Wait to continue."
            : paymentMethodsUnavailable
              ? "Saved cards could not be refreshed. Change payment to retry."
              : chargeReadySelectedPmId === null
                ? "Your saved card is no longer available. Change payment to continue."
                : null
        : null;

  useEffect(() => {
    if (!freshCardFinalActionBlocked || !finalActionFocusedRef.current) {
      return;
    }
    const activeElement = document.activeElement;
    if (
      activeElement === finalActionRef.current ||
      activeElement === document.body ||
      activeElement === document.documentElement
    ) {
      restoreFinalActionFocusRef.current = true;
    } else {
      finalActionFocusedRef.current = false;
    }
  }, [freshCardFinalActionBlocked]);

  useEffect(() => {
    if (!freshCardFinalActionBlocked || !restoreFinalActionFocusRef.current) {
      return;
    }
    const cancelRestoreForNewFocus = (event: globalThis.FocusEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        target !== finalActionRef.current &&
        target !== document.body &&
        target !== document.documentElement
      ) {
        finalActionFocusedRef.current = false;
        restoreFinalActionFocusRef.current = false;
      }
    };
    document.addEventListener("focusin", cancelRestoreForNewFocus);
    return () => {
      document.removeEventListener("focusin", cancelRestoreForNewFocus);
    };
  }, [freshCardFinalActionBlocked]);

  useEffect(() => {
    if (freshCardFinalActionBlocked || !restoreFinalActionFocusRef.current) {
      return;
    }
    restoreFinalActionFocusRef.current = false;
    if (checkoutStep !== "review" || ctaDisabled) return;
    const activeElement = document.activeElement;
    if (
      activeElement === document.body ||
      activeElement === document.documentElement
    ) {
      finalActionRef.current?.focus();
    }
  }, [checkoutStep, ctaDisabled, freshCardFinalActionBlocked]);

  useEffect(() => {
    if (!freshCardFinalActionBlocked || !restorePaymentFocusRef.current) {
      return;
    }
    const cancelRestoreForNewFocus = (event: globalThis.FocusEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        target !== paymentFocusTargetRef.current &&
        target !== document.body &&
        target !== document.documentElement
      ) {
        paymentFocusTargetRef.current = null;
        restorePaymentFocusRef.current = false;
      }
    };
    document.addEventListener("focusin", cancelRestoreForNewFocus);
    return () => {
      document.removeEventListener("focusin", cancelRestoreForNewFocus);
    };
  }, [freshCardFinalActionBlocked]);

  useEffect(() => {
    if (freshCardFinalActionBlocked || !restorePaymentFocusRef.current) {
      return;
    }
    restorePaymentFocusRef.current = false;
    const target = paymentFocusTargetRef.current;
    paymentFocusTargetRef.current = null;
    if (
      checkoutStep !== "payment" ||
      !target?.isConnected ||
      target.matches(":disabled")
    ) {
      return;
    }
    const activeElement = document.activeElement;
    if (
      activeElement === document.body ||
      activeElement === document.documentElement
    ) {
      target.focus();
    }
  }, [checkoutStep, freshCardFinalActionBlocked]);

  const facilityName =
    currentPaymentOptions?.facility_name.trim() ||
    "Facility details unavailable";
  const selectedPaymentLabel = (() => {
    if (recoveringUnavailableAttempt) return "Previous payment attempt";
    if (!selection) return "Choose how to pay";
    if (selection.method === "membership") {
      const option = currentPaymentOptions?.active_memberships.find(
        (membership) => membership.subscription_id === selection.subscriptionId,
      );
      return option ? `Membership · ${option.option_label}` : "Membership";
    }
    if (selection.method === "package") {
      const option = currentPaymentOptions?.active_packages.find(
        (visitPackage) =>
          visitPackage.subscription_id === selection.subscriptionId,
      );
      return option
        ? `${option.option_label} · 1 visit`
        : "Visit pass · 1 visit";
    }
    if (!singleRequiresPaymentMethod) return "No payment method required";
    return selectedCard
      ? `${selectedCard.brand} ···· ${selectedCard.last4}`
      : "Saved card";
  })();
  const price = dropInPricePresentation(
    currentPaymentOptions,
    selection,
    recoveringUnavailableAttempt,
  );
  const priceLabel = price.total;

  async function reconcileAddedCard(paymentMethodId: string) {
    setPendingCardPmId(paymentMethodId);
    setCardReconcileError(null);
    setCardSavePending(true);
    try {
      await queryClient.cancelQueries({
        queryKey: walletQueryKey,
        exact: true,
      });
      const refreshed = await methodsQ.refetch({ cancelRefetch: true });
      if (!refreshed.isSuccess) {
        setCardReconcileError(
          "Your card was saved, but we couldn't refresh it yet. Retry before continuing.",
        );
        return;
      }
      const savedMethod = refreshed.data?.find(
        (method) => method.payment_method_id === paymentMethodId,
      );
      if (savedMethod?.is_usable === true) {
        setSelectedPmId(paymentMethodId);
        setPendingCardPmId(null);
        focusNewCardOnMount.current = false;
        setAddingCard(false);
        goToStep("review");
        return;
      }
      if (savedMethod) {
        setCardReconcileError(
          "Your card was saved, but it isn't ready to use. Retry the refresh or use another card.",
        );
        return;
      }
      setCardReconcileError(
        "Your card was saved, but we couldn't refresh it yet. Retry before continuing.",
      );
    } finally {
      setCardSavePending(false);
    }
  }

  function handleReview() {
    if (
      !paymentOptionsReady ||
      checkInBody === null ||
      paymentSelectionLocked ||
      !isOpenNow
    ) {
      return;
    }
    setAddingCard(false);
    goToStep("review");
  }

  function handleChangePayment() {
    if (paymentSelectionLocked || paymentAuthentication) return;
    setAddingCard(false);
    setCardReconcileError(null);
    goToStep("payment");
  }

  function claimAttemptOperation(attempt: CheckInAttempt): CheckInAttempt {
    const claimedAttempt = {
      ...attempt,
      operationToken: globalThis.crypto.randomUUID(),
    };
    window.sessionStorage.setItem(
      attemptStorageKey(facilityId),
      JSON.stringify(claimedAttempt),
    );
    setCheckInAttempt(claimedAttempt);
    return claimedAttempt;
  }

  function clearAttempt(attempt: CheckInAttempt) {
    removeStoredCheckInAttempt(facilityId, attempt);
    setCheckInAttempt((current) =>
      current?.userId === attempt.userId &&
      current.idempotencyKey === attempt.idempotencyKey &&
      current.operationToken === attempt.operationToken
        ? null
        : current,
    );
  }

  function failCloseCardPayments(attempt: CheckInAttempt) {
    clearAttempt(attempt);
    setCardPaymentsRejected(true);
    setAddingCard(false);
    setSelectedPmId(null);
    setPaymentAuthentication(null);
    goToStep("payment");
    void paymentOptions.refetch();
  }

  function failCloseAdmission(attempt: CheckInAttempt) {
    clearAttempt(attempt);
    admissionRecoveryPhase.current = courtState.isActorRevisionPending
      ? "awaiting_pending_clear"
      : "awaiting_revision";
    setAdmissionRejected(true);
    setPaymentAuthentication(null);
    void paymentOptions.refetch();
  }

  function handleCheckIn() {
    if (checkInFlight.current) return;
    const livePaymentOptions = queryClient.getQueryData<DropInPaymentOption>(
      queryKeys.dropin.paymentOptions(facilityId),
    );
    if (
      checkInAttempt === null &&
      (admissionRejected ||
        courtState.isActorRevisionPending ||
        livePaymentOptions?.active_now !== true)
    ) {
      return;
    }
    const liveWalletState = queryClient.getQueryState(walletQueryKey);
    const liveWalletMethods =
      queryClient.getQueryData<SavedPaymentMethodResponse[]>(walletQueryKey);
    const liveChargeReadyPmId = !needsSingleCard
      ? selectedPmId
      : onlineManager.isOnline() &&
          !walletValidationRequiredRef.current &&
          liveWalletState?.status === "success" &&
          liveWalletState.fetchStatus === "idle" &&
          liveWalletState.isInvalidated === false &&
          liveWalletMethods?.some(
            (method) =>
              method.payment_method_id === selectedPmId && method.is_usable,
          )
        ? selectedPmId
        : null;
    const liveCheckInBody = buildCheckInRequest(
      selection,
      liveChargeReadyPmId,
      singleRequiresPaymentMethod,
      currentPaymentOptions?.price_quote_version ?? null,
      currentPaymentOptions?.checkout_terms,
      currentPaymentOptions?.campaign?.campaign_id ?? null,
    );
    // Fresh submissions read the wallet cache at action time. Persisted
    // attempts remain immutable and do not depend on an unrelated wallet GET.
    const candidateAttempt =
      checkInAttempt ??
      (liveCheckInBody === null || !userId
        ? null
        : {
            userId,
            idempotencyKey: globalThis.crypto.randomUUID(),
            body: liveCheckInBody,
          });
    if (ctaDisabled || candidateAttempt === null) return;
    const operationId = ++operationGeneration.current;
    const attempt = claimAttemptOperation(candidateAttempt);
    checkInFlight.current = true;
    checkIn.mutate(
      { ...attempt.body, idempotency_key: attempt.idempotencyKey },
      {
        onSuccess: () => {
          if (operationGeneration.current === operationId) {
            clearAttempt(attempt);
          }
        },
        onError: (error) => {
          if (operationGeneration.current !== operationId) return;
          if (isApiError(error) && error.code === "NO_ACTIVE_DROP_IN") {
            failCloseAdmission(attempt);
            return;
          }
          if (
            isApiError(error) &&
            (error.code === "DROPIN_PRICE_CHANGED" ||
              error.code === "DROPIN_POLICY_CHANGED")
          ) {
            clearAttempt(attempt);
            void paymentOptions.refetch();
            return;
          }
          if (
            isApiError(error) &&
            error.code === "FACILITY_PAYMENTS_UNAVAILABLE"
          ) {
            failCloseCardPayments(attempt);
            return;
          }
          if (
            isApiError(error) &&
            error.code === "DROPIN_WAIVER_CHANGED_AFTER_PAYMENT"
          ) {
            clearAttempt(attempt);
            setPaymentAuthentication(null);
            void waiver.retryStatus();
            return;
          }
          const authentication = getPaymentAuthenticationRequired(error);
          if (authentication) {
            setPaymentAuthentication({
              clientSecret: authentication.clientSecret,
              attempt,
              operationId,
            });
          } else if (isApiError(error) && error.status === 428) {
            void waiver.retryStatus();
          } else if (isTerminalPaymentAttemptError(error)) {
            clearAttempt(attempt);
          }
        },
        onSettled: () => {
          checkInFlight.current = false;
        },
      },
    );
  }

  async function finishPaymentAuthentication(
    authenticatedClientSecret: string,
  ) {
    const authentication = paymentAuthentication;
    if (
      !authentication ||
      authentication.clientSecret !== authenticatedClientSecret ||
      operationGeneration.current !== authentication.operationId
    ) {
      return;
    }
    if (checkInFlight.current) return;
    const operationId = ++operationGeneration.current;
    const attempt = claimAttemptOperation(authentication.attempt);
    checkInFlight.current = true;
    setPaymentAuthentication((current) =>
      current?.clientSecret === authenticatedClientSecret &&
      current.operationId === authentication.operationId
        ? { ...current, attempt, operationId }
        : current,
    );
    try {
      await checkIn.mutateAsync({
        ...attempt.body,
        idempotency_key: attempt.idempotencyKey,
      });
      if (operationGeneration.current !== operationId) return;
      clearAttempt(attempt);
      setPaymentAuthentication((current) =>
        current?.clientSecret === authenticatedClientSecret ? null : current,
      );
    } catch (error) {
      if (operationGeneration.current !== operationId) return;
      if (isApiError(error) && error.code === "FACILITY_PAYMENTS_UNAVAILABLE") {
        failCloseCardPayments(attempt);
        throw error;
      }
      if (isApiError(error) && error.code === "NO_ACTIVE_DROP_IN") {
        failCloseAdmission(attempt);
        throw error;
      }
      if (
        isApiError(error) &&
        error.code === "DROPIN_WAIVER_CHANGED_AFTER_PAYMENT"
      ) {
        clearAttempt(attempt);
        setPaymentAuthentication((current) =>
          current?.clientSecret === authenticatedClientSecret ? null : current,
        );
        void waiver.retryStatus();
      } else if (isApiError(error) && error.status === 428) {
        setPaymentAuthentication((current) =>
          current?.clientSecret === authenticatedClientSecret ? null : current,
        );
        void waiver.retryStatus();
      } else {
        setPaymentAuthentication((current) =>
          updatePaymentAuthenticationForError(
            current,
            authenticatedClientSecret,
            error,
          ),
        );
        if (
          isApiError(error) &&
          (error.code === "DROPIN_PRICE_CHANGED" ||
            error.code === "DROPIN_POLICY_CHANGED")
        ) {
          void paymentOptions.refetch();
        }
        if (isTerminalPaymentAttemptError(error)) {
          clearAttempt(attempt);
        }
      }
      throw error;
    } finally {
      checkInFlight.current = false;
    }
  }

  if (activeSession) {
    return (
      <Page>
        <Typography color="text.secondary" role="status">
          Opening your drop-in session…
        </Typography>
      </Page>
    );
  }

  return (
    <Page>
      <Stack
        spacing={3}
        sx={{ "& .MuiButton-root.Mui-disabled": { pointerEvents: "auto" } }}
      >
        <DropInCheckInHeader
          facilityId={facilityId}
          facilityName={currentPaymentOptions?.facility_name.trim()}
          paymentInFlight={
            paymentSelectionLocked || paymentAuthentication !== null
          }
          isSelfServe={isSelfServe}
        />

        {/* No drop-in price configured (backend NO_DROPIN_PRICE 422). A setup gap,
          NOT the transient closed-window state — so it carries its own exact
          copy and replaces the payment form (there is nothing to pay for). */}
        {showNoPrice ? (
          <Paper variant="outlined" role="status" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Drop-in
            </Typography>
            <Typography>
              This facility hasn&apos;t set a drop-in price yet — check-in
              isn&apos;t available.
            </Typography>
          </Paper>
        ) : null}

        {/* The check-in form only shows when there's no active session yet and the
          facility actually has a drop-in price. */}
        {!activeSession && !showNoPrice ? (
          <Stack spacing={3}>
            {paymentOptions.isLoading ? (
              <Stack
                direction="row"
                spacing={1}
                role="status"
                sx={{ alignItems: "center" }}
              >
                <CircularProgress size={18} />
                <Typography color="text.secondary">
                  Loading payment options…
                </Typography>
              </Stack>
            ) : !currentPaymentOptions ? (
              <Alert severity="error">
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ alignItems: { sm: "center" } }}
                >
                  <Typography sx={{ flex: 1 }}>
                    We couldn&apos;t load payment options.
                  </Typography>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => void paymentOptions.refetch()}
                  >
                    Retry
                  </Button>
                </Stack>
              </Alert>
            ) : checkoutStep === "payment" ? (
              <>
                <DropInOrderSummary
                  facilityName={facilityName}
                  playerName={playerName}
                  priceLabel={priceLabel}
                  price={price}
                />
                <Box component="section" aria-labelledby="sec-pay">
                  <Typography variant="overline" color="text.secondary">
                    How you&apos;re paying
                  </Typography>
                  <Typography
                    component="h2"
                    variant="h5"
                    id="sec-pay"
                    ref={paymentHeadingRef}
                    tabIndex={-1}
                  >
                    Payment
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1.5 }}>
                    <PaymentMethodSelect
                      options={currentPaymentOptions}
                      value={selection}
                      onChange={setSelection}
                      disabled={paymentSelectionLocked}
                    />

                    {needsSingleCard && isOpenNow ? (
                      <Stack spacing={1.5}>
                        {configQ.isError ? (
                          <Alert severity="error">
                            This facility can&apos;t take card payments yet.
                            Please try again later.
                          </Alert>
                        ) : cardEntryOpen && configQ.data ? (
                          <>
                            {showWalletReadError ? (
                              <Box
                                ref={walletRetryRegionRef}
                                onBlurCapture={handleWalletRetryBlur}
                                onFocusCapture={handleWalletRetryFocus}
                              >
                                <PaymentMethodsLoadError
                                  retrying={walletRefetchPending}
                                  onRetry={() => void retryPaymentMethods()}
                                />
                              </Box>
                            ) : methodsQ.isLoading ? (
                              <Typography color="text.secondary" role="status">
                                Loading your saved cards…
                              </Typography>
                            ) : methods.length > 0 ? (
                              <Button
                                ref={useSavedCardAfterRetryRef}
                                type="button"
                                variant="outlined"
                                size="small"
                                disabled={cardSelectionLocked}
                                onClick={() => {
                                  focusNewCardOnMount.current = false;
                                  focusSavedCardAfterRender.current = true;
                                  setAddingCard(false);
                                  setCardReconcileError(null);
                                  setPendingCardPmId(null);
                                }}
                              >
                                Use a saved card
                              </Button>
                            ) : null}
                            {pendingCardPmId ? (
                              cardReconcileError ? (
                                <Alert severity="error">
                                  <Stack
                                    spacing={1}
                                    sx={{ alignItems: "flex-start" }}
                                  >
                                    <Typography>
                                      {cardReconcileError}
                                    </Typography>
                                    <Button
                                      type="button"
                                      variant="outlined"
                                      size="small"
                                      disabled={cardSavePending}
                                      onClick={() =>
                                        void reconcileAddedCard(pendingCardPmId)
                                      }
                                    >
                                      Retry refresh
                                    </Button>
                                  </Stack>
                                </Alert>
                              ) : (
                                <Typography
                                  color="text.secondary"
                                  role="status"
                                >
                                  Refreshing your saved cards…
                                </Typography>
                              )
                            ) : (
                              <Box
                                aria-label="New card details"
                                ref={newCardEntryRef}
                                tabIndex={-1}
                              >
                                {newCardForm}
                              </Box>
                            )}
                          </>
                        ) : configQ.isLoading || methodsQ.isLoading ? (
                          <Typography color="text.secondary" role="status">
                            Loading your saved cards…
                          </Typography>
                        ) : paymentMethodsUnavailable ? (
                          <Box
                            ref={walletRetryRegionRef}
                            onBlurCapture={handleWalletRetryBlur}
                            onFocusCapture={handleWalletRetryFocus}
                          >
                            <PaymentMethodsLoadError
                              retrying={walletRefetchPending}
                              onRetry={() => void retryPaymentMethods()}
                            />
                          </Box>
                        ) : (
                          <Stack
                            spacing={1.5}
                            ref={savedCardsRef}
                            tabIndex={-1}
                          >
                            <MaterialPaymentMethodPicker
                              methods={methods}
                              selectedId={selectedPmId}
                              onSelect={setSelectedPmId}
                              disabled={cardSelectionLocked}
                              allowRemove
                              facilityId={facilityId}
                              paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(
                                facilityId,
                              )}
                            />
                            {configQ.data ? (
                              <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                disabled={cardSelectionLocked}
                                onClick={() => {
                                  focusNewCardOnMount.current = true;
                                  setAddingCard(true);
                                  setCardReconcileError(null);
                                  setPendingCardPmId(null);
                                }}
                              >
                                Add a new card
                              </Button>
                            ) : null}
                          </Stack>
                        )}
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>

                {cardEntryOpen ? null : (
                  <Stack
                    spacing={1}
                    sx={{
                      position: { xs: "static", md: "sticky" },
                      bottom: { md: 0 },
                      py: 1.5,
                      bgcolor: "background.default",
                      alignItems: "flex-start",
                    }}
                  >
                    {reviewDisabledReason ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        id={reviewDisabledReasonId}
                      >
                        {reviewDisabledReason}
                      </Typography>
                    ) : null}
                    <Button
                      ref={paymentReviewActionRef}
                      aria-describedby={
                        reviewDisabledReason
                          ? reviewDisabledReasonId
                          : undefined
                      }
                      type="button"
                      variant="contained"
                      disabled={reviewDisabled}
                      onClick={handleReview}
                    >
                      Review check-in
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <>
                <Box component="section" aria-labelledby="sec-review">
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ alignItems: { sm: "flex-start" } }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="overline" color="text.secondary">
                        Before you confirm
                      </Typography>
                      <Typography
                        component="h2"
                        variant="h5"
                        id="sec-review"
                        ref={reviewHeadingRef}
                        tabIndex={-1}
                      >
                        Review and confirm
                      </Typography>
                    </Box>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      disabled={
                        paymentSelectionLocked || !!paymentAuthentication
                      }
                      onClick={handleChangePayment}
                    >
                      Change payment
                    </Button>
                  </Stack>

                  <Box sx={{ mt: 1.5 }}>
                    <DropInOrderSummary
                      facilityName={facilityName}
                      playerName={playerName}
                      priceLabel={priceLabel}
                      paymentLabel={selectedPaymentLabel}
                      price={price}
                    />
                  </Box>
                </Box>

                <Box component="section" aria-labelledby="sec-terms">
                  <Typography variant="overline" color="text.secondary">
                    Cancellation and refunds
                  </Typography>
                  <Typography component="h2" variant="h5" id="sec-terms">
                    Terms
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    {currentPaymentOptions.checkout_terms.summary}
                  </Typography>
                </Box>

                <Box component="section" aria-labelledby="sec-waiver">
                  <Typography variant="overline" color="text.secondary">
                    Participation
                  </Typography>
                  <Typography component="h2" variant="h5" id="sec-waiver">
                    Waiver
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    {waiver.statusError ? (
                      <Alert severity="error">
                        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                          <Typography variant="body2">
                            We couldn&apos;t confirm your waiver, so we
                            can&apos;t check you in yet. Please retry.
                          </Typography>
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={waiver.retryStatus}
                          >
                            Retry
                          </Button>
                        </Stack>
                      </Alert>
                    ) : waiver.isLoading ? (
                      <Typography color="text.secondary" role="status">
                        Confirming your waiver…
                      </Typography>
                    ) : waiver.isBlocked ? (
                      <MaterialWaiverGate
                        currentWaiver={waiver.currentWaiver}
                        accepting={waiver.isAccepting}
                        acceptError={waiver.acceptError}
                        onAccept={waiver.accept}
                      />
                    ) : waiver.currentVersionId !== null ? (
                      <MaterialWaiverGate
                        accepted
                        currentWaiver={waiver.currentWaiver}
                        accepting={waiver.isAccepting}
                        acceptError={waiver.acceptError}
                        onAccept={waiver.accept}
                      />
                    ) : (
                      <Typography color="text.secondary" role="status">
                        No waiver required.
                      </Typography>
                    )}
                  </Box>
                </Box>

                {checkInError ? (
                  <Alert severity="error">{checkInError}</Alert>
                ) : null}

                {recoveringUnavailableAttempt ? (
                  <Typography color="text.secondary" role="status">
                    A previous check-in needs to be reconciled before payment
                    choices can change.
                  </Typography>
                ) : null}

                {paymentAuthentication && configQ.data ? (
                  <MaterialConnectedElementsProvider config={configQ.data}>
                    <FormControl
                      component="fieldset"
                      aria-label="Payment authentication controls"
                      disabled={checkIn.isPending}
                      fullWidth
                    >
                      <MaterialPaymentIntentAuthentication
                        clientSecret={paymentAuthentication.clientSecret}
                        onAuthenticated={finishPaymentAuthentication}
                      />
                    </FormControl>
                  </MaterialConnectedElementsProvider>
                ) : null}

                {paymentAuthentication ? null : (
                  <Stack
                    spacing={1}
                    sx={{
                      position: { xs: "static", md: "sticky" },
                      bottom: { md: 0 },
                      py: 1.5,
                      bgcolor: "background.default",
                      alignItems: "flex-start",
                    }}
                  >
                    {finalActionDisabledReason ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        id={finalActionDisabledReasonId}
                      >
                        {finalActionDisabledReason}
                      </Typography>
                    ) : null}
                    <Button
                      ref={finalActionRef}
                      aria-describedby={
                        finalActionDisabledReason
                          ? finalActionDisabledReasonId
                          : undefined
                      }
                      type="button"
                      variant="contained"
                      disabled={ctaDisabled}
                      onBlur={handleFinalActionBlur}
                      onClick={handleCheckIn}
                      onFocus={handleFinalActionFocus}
                    >
                      {recoveringUnavailableAttempt
                        ? "Resume check-in"
                        : isSelfServe
                          ? "Check myself in & pick a court"
                          : "Check myself in"}
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        ) : null}

        {/* The facility's community ("club"), if one exists. A SECONDARY section
          that routes into /community/communities/:id (which owns join/leave +
          feed); it self-hides when the facility has no community and never gates
          this page. */}
        <FacilityCommunityCard facilityId={facilityId} />
      </Stack>
    </Page>
  );
}
